import './Hero.css';

import classNames from 'classnames';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useIsHemX } from 'gatsby-theme-hem-store/src/hooks/useIsHemX';
import React from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { Layout, Theme } from './hero-types.d';
import HeroCenteredContent from './HeroCenteredContent';
import HeroLeftContent from './HeroLeftContent';
import HeroMarqeeContent from './HeroMarqeeContent';
import HeroSplitContent from './HeroSplitContent';

type Props = {
  hero: Queries.HomepageHeroFragment;
  isHomepage?: boolean;
  titleTag?: 'h1' | 'h2' | 'h3';
};

export const Hero: React.FC<Props> = ({
  hero,
  isHomepage = false,
  titleTag = 'h1',
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)', {
    initializeWithValue: false,
    defaultValue: true,
  });

  const { isHemX } = useIsHemX();
  const theme = hero?.theme || Theme.WHITE;

  let heroThemeStyle;
  switch (theme) {
    case Theme.HEM_X:
      heroThemeStyle = {
        '--hero-headline-color': 'var(--color-hem-x)',
        '--hero-text-color': 'var(--color-hem-x)',
        '--hero-button-color': 'var(--color-blue)',
        '--hero-button-text-color': 'var(--color-white)',
        '--hero-button-hover-color': '#030bb3',
        '--hero-button-hover-text-color': 'var(--color-white)',
      } as React.CSSProperties;
      break;
    case Theme.YELLOW:
      heroThemeStyle = {
        '--hero-headline-color': 'var(--color-yellow-gloss)',
        '--hero-text-color': 'var(--color-yellow-gloss)',
        '--hero-button-color': 'var(--color-white)',
        '--hero-button-text-color': 'var(--color-black)',
        '--hero-button-hover-color': 'var(--color-gray-light)',
        '--hero-button-hover-text-color': 'var(--color-black)',
      } as React.CSSProperties;
      break;
    case Theme.RED:
      heroThemeStyle = {
        '--hero-headline-color': 'var(--color-red-gloss)',
        '--hero-text-color': 'var(--color-red-gloss)',
        '--hero-button-color': 'var(--color-red-matte)',
        '--hero-button-text-color': 'var(--color-red-gloss)',
        '--hero-button-hover-color': 'var(--color-gray-light)',
        '--hero-button-hover-text-color': 'var(--color-red-gloss)',
      } as React.CSSProperties;
      break;
    case Theme.GREEN:
      heroThemeStyle = {
        '--hero-headline-color': 'var(--color-green-matte)',
        '--hero-text-color': 'var(--color-green-matte)',
        '--hero-button-color': 'var(--color-green-gloss)',
        '--hero-button-text-color': 'var(--color-green-matte)',
        '--hero-button-hover-color': 'var(--color-black)',
        '--hero-button-hover-text-color': 'var(--color-green-matte)',
      } as React.CSSProperties;
      break;
    case Theme.BLUE:
      heroThemeStyle = {
        '--hero-headline-color': 'var(--color-blue)',
        '--hero-text-color': 'var(--color-blue)',
        '--hero-button-color': 'var(--color-white)',
        '--hero-button-text-color': 'var(--color-blue)',
        '--hero-button-hover-color': 'var(--color-gray-light)',
        '--hero-button-hover-text-color': 'var(--color-blue)',
      } as React.CSSProperties;
      break;
    case Theme.BLACK:
      heroThemeStyle = {
        '--hero-headline-color': 'var(--color-black)',
        '--hero-text-color': 'var(--color-black)',
        '--hero-button-color': 'var(--color-black)',
        '--hero-button-text-color': 'var(--color-white)',
        '--hero-button-hover-color': '#3b3c3f',
        '--hero-button-hover-text-color': 'var(--color-white)',
      } as React.CSSProperties;
      break;
    case Theme.WHITE:
    default:
      heroThemeStyle = {
        '--hero-headline-color': 'var(--color-white)',
        '--hero-text-color': 'var(--color-white)',
        '--hero-button-color': 'var(--color-white)',
        '--hero-button-text-color': 'var(--color-black)',
        '--hero-button-hover-color': 'var(--color-gray-light)',
        '--hero-button-hover-text-color': 'var(--color-black)',
      } as React.CSSProperties;
  }

  const renderContent = (layout: Layout) => {
    switch (layout) {
      case Layout.LEFT:
        return (
          <HeroLeftContent hero={hero} titleTag={titleTag} theme={theme} />
        );
      case Layout.MARQEE:
        return (
          <HeroMarqeeContent hero={hero} titleTag={titleTag} theme={theme} />
        );
      case Layout.SPLIT:
        return (
          <HeroSplitContent
            hero={hero}
            titleTag={titleTag}
            isHomepage={isHomepage}
          />
        );
      default:
        return (
          <HeroCenteredContent hero={hero} titleTag={titleTag} theme={theme} />
        );
    }
  };

  if (isHemX) {
    heroThemeStyle = {
      '--hero-headline-color': 'var(--color-hem-x)',
      '--hero-text-color': 'var(--color-hem-x)',
      '--hero-button-color': 'var(--color-blue)',
      '--hero-button-text-color': 'var(--color-white)',
      '--hero-button-hover-color': '#030bb3',
      '--hero-button-hover-text-color': 'var(--color-white)',
    } as React.CSSProperties;
  }

  return (
    <div
      className={classNames('@container/hero', {
        'col-span-full': !isHomepage,
        'mt-[--spacing-page-edges]': !isHomepage && !isHemX,
        '-mx-[--spacing-page-edges]': isHemX,
      })}
      style={heroThemeStyle}
    >
      {(hero.layout as Layout) === Layout.SPLIT ? (
        renderContent(hero.layout as Layout)
      ) : (
        <div
          className={classNames('relative grid h-full overflow-hidden', {
            'aspect-square': !isHomepage,
            '@4xl/hero:aspect-[1856/761]': !isHomepage,
            'aspect-[4/5]': isHomepage,
            '@4xl/hero:aspect-[4/3]': isHomepage,
            '@7xl/hero:aspect-[16/9]': isHomepage,
          })}
        >
          {hero?.image?.url?.endsWith('.mp4') ? (
            <video
              src={
                isDesktop
                  ? hero.image.url
                  : hero.mobileImage.url ?? hero.image.url
              }
              className="absolute inset-0 h-full w-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            />
          ) : (
            <>
              <GatsbyImage
                alt=""
                className="!absolute inset-0 h-full w-full object-cover @4xl/hero:!hidden"
                image={hero.mobileImage?.gatsbyImageData}
              />
              <GatsbyImage
                alt=""
                className="!absolute inset-0 !hidden h-full w-full object-cover @4xl/hero:!block"
                image={hero.image?.gatsbyImageData}
              />
            </>
          )}

          <div className="absolute inset-0 bg-black opacity-5" />

          <div
            className={classNames(
              'relative grid items-center px-4 py-8 md:px-8',
              {
                'md:py-16': !isHemX,
              },
            )}
          >
            {isHemX ? (
              <>
                {!!hero.title && (
                  <h1 className="hero-headline-h2 self-baseline text-balance">
                    {hero.title}
                  </h1>
                )}

                {!!hero.description && (
                  <div className="mt-4 max-w-5xl self-end text-balance text-xl font-bold text-[color:--hero-text-color] md:text-2xl">
                    {hero.description}
                  </div>
                )}
              </>
            ) : (
              renderContent(hero.layout as Layout)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const query = graphql`
  fragment HomepageHero on ContentfulComponentHero {
    __typename
    description
    theme
    layout
    image {
      url
      gatsbyImageData(
        formats: [AUTO, AVIF, WEBP]
        quality: 80
        placeholder: BLURRED
        resizingBehavior: FILL
        layout: FULL_WIDTH
      )
    }
    mobileImage {
      url
      gatsbyImageData(
        formats: [AUTO, AVIF, WEBP]
        quality: 80
        placeholder: BLURRED
        resizingBehavior: FILL
        layout: FULL_WIDTH
      )
    }
    title
    linkLabel
    link {
      ... on ContentfulLink {
        ...Link
      }
      ... on ContentfulPage {
        slug
      }
      ... on ContentfulPageCategory {
        slug
      }
      ... on ContentfulPageDepartment {
        slug
      }
      ... on ContentfulProductVariant {
        slug
      }
    }
    secondaryLinkLabel
    secondaryLink {
      ... on ContentfulLink {
        ...Link
      }
      ... on ContentfulPage {
        slug
      }
      ... on ContentfulPageCategory {
        slug
      }
      ... on ContentfulPageDepartment {
        slug
      }
      ... on ContentfulProductVariant {
        slug
      }
    }
    countdownTimer
  }
`;
